import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { ErrorResponse, PaginatedResponse } from '../../api';
import { LikeType } from '../../common/model/LikeType';
import {
  deleteCommentOnStory,
  fetchCommentsForStory,
  fetchRepliesToCommentsOnStory,
  fetchReplyToStoryComment,
  fetchSelectedStoryComment,
  fetchStoryComment,
  insertCommentOnStory,
  insertReplyToCommentOnStory,
  likeStoryComment,
  moderatorDeleteCommentOnStory,
  moderatorUpdateCommentOnStory,
  updateCommentOnStory,
} from './actions';
import { StoryComment } from './model';

const likeUnlikeComment = (comment: StoryComment, increment: boolean) => {
  if (comment.member) {
    comment.member.memberLike = increment;
  }
};

const incrementDecrementSentiment = (
  comment: StoryComment,
  sentiment: LikeType,
  increment: boolean
) => {
  const incrementValue = comment.member?.memberLikeValue === sentiment ? -1 : 1;

  if (
    comment.member?.memberLikeValue &&
    comment.member?.memberLikeValue !== sentiment
  ) {
    incrementDecrementSentiment(
      comment,
      comment.member?.memberLikeValue as LikeType,
      !increment
    );
  }

  switch (sentiment) {
    case LikeType.like:
      comment.likeCount =
        comment.likeCount + incrementValue >= 0
          ? comment.likeCount + incrementValue
          : comment.likeCount;
      break;
    case LikeType.care:
      comment.careCount =
        comment.careCount + incrementValue >= 0
          ? comment.careCount + incrementValue
          : comment.careCount;
      break;
    case LikeType.helpful:
      comment.helpfulCount =
        comment.helpfulCount + incrementValue >= 0
          ? comment.helpfulCount + incrementValue
          : comment.helpfulCount;
      break;

    default:
      break;
  }
};

export interface StoriesCommentsState {
  comments: {
    [storyId: string]: PaginatedResponse<StoryComment[]>;
  };
  loadingComments: {
    [storyId: string]: boolean;
  };
  repliesToComments: {
    [commentId: string]: PaginatedResponse<StoryComment[]>;
  };
  loadingRepliesToComments: {
    [commentId: string]: boolean;
  };
  loadingInsertComment: boolean;
  loadingInsertReplyToComment: {
    [commentId: string]: boolean;
  };
  loadingUpdateComment: {
    [storyId: string]: boolean;
  };
  focusedComment?: StoryComment;
  selectedComment?: StoryComment;
  selectedCommentLoading?: boolean;
  selectedCommentError?: ErrorResponse;
  focusedReplyToStoryComment?: StoryComment;
  editedCommentOnStory?: StoryComment;
}

const initialState: StoriesCommentsState = {
  comments: {},
  loadingComments: {},
  repliesToComments: {},
  loadingRepliesToComments: {},
  loadingInsertComment: false,
  loadingInsertReplyToComment: {},
  loadingUpdateComment: {},
  focusedComment: undefined,
  selectedComment: undefined,
  selectedCommentLoading: undefined,
  selectedCommentError: undefined,
  focusedReplyToStoryComment: undefined,
  editedCommentOnStory: undefined,
};

const storiesCommentsSlice = createSlice({
  name: 'storiesComments',
  initialState,
  reducers: {
    clearComments: (state) => {
      state.comments = {};
    },
    clearRepliesToCommentStory: (state) => {
      state.repliesToComments = {};
    },
    clearFocusedStoryComment: (state) => {
      state.focusedComment = undefined;
    },
    clearSelectedStoryComment: (state) => {
      state.selectedComment = undefined;
    },
    clearFocusedReplyToStoryComment: (state) => {
      state.focusedReplyToStoryComment = undefined;
    },
    incrementStoryCommentRepliesCount: (
      state,
      action: PayloadAction<{
        parentId: string;
        targetId: string;
      }>
    ) => {
      const index = state.comments[action.payload.parentId].data.findIndex(
        (el) => el.id === action.payload.targetId
      );
      if (index >= 0) {
        state.comments[action.payload.parentId].data[index].commentCount += 1;
      }

      if (
        state.selectedComment &&
        state.selectedComment.id === action.payload.targetId
      ) {
        state.selectedComment.commentCount += 1;
      }
    },
    decrementStoryCommentRepliesCount: (
      state,
      action: PayloadAction<{
        parentId: string;
        targetId: string;
      }>
    ) => {
      const index = state.comments[action.payload.parentId].data.findIndex(
        (el) => el.id === action.payload.targetId
      );
      if (index >= 0) {
        state.comments[action.payload.parentId].data[index].commentCount -= 1;
      }

      if (
        state.selectedComment &&
        state.selectedComment.id === action.payload.targetId
      ) {
        state.selectedComment.commentCount -= 1;
      }
    },
    setEditedCommentOnStory: (
      state,
      action: PayloadAction<StoryComment | undefined>
    ) => {
      state.editedCommentOnStory = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCommentsForStory.pending, (state, action) => {
        state.loadingComments[action.meta.arg.storyId] = true;
      })
      .addCase(fetchCommentsForStory.rejected, (state, action) => {
        state.loadingComments[action.meta.arg.storyId] = false;
      })
      .addCase(fetchCommentsForStory.fulfilled, (state, action) => {
        let commentsToStory =
          state.comments[action.meta.arg.storyId]?.data ?? [];
        if (action.meta.arg.xMsContinuationToken) {
          commentsToStory.push(...action.payload.data);
        } else {
          commentsToStory = [...action.payload.data];
        }
        state.comments[action.meta.arg.storyId] = {
          data: commentsToStory,
          xMsContinuationToken: action.payload?.xMsContinuationToken,
        };
        state.loadingComments[action.meta.arg.storyId] = false;
      })

      .addCase(insertCommentOnStory.pending, (state) => {
        state.loadingInsertComment = true;
      })
      .addCase(insertCommentOnStory.rejected, (state) => {
        state.loadingInsertComment = false;
      })
      .addCase(insertCommentOnStory.fulfilled, (state, action) => {
        state.loadingInsertComment = false;
        if (!state.comments[action.meta.arg.storyId])
          state.comments[action.meta.arg.storyId] = {
            data: [],
          };
        state.comments[action.meta.arg.storyId].data.unshift(action.payload);
      })

      .addCase(fetchRepliesToCommentsOnStory.pending, (state, action) => {
        state.loadingRepliesToComments[action.meta.arg.commentId] = true;
      })
      .addCase(fetchRepliesToCommentsOnStory.rejected, (state, action) => {
        state.loadingRepliesToComments[action.meta.arg.commentId] = false;
      })
      .addCase(fetchRepliesToCommentsOnStory.fulfilled, (state, action) => {
        let repliesToComments =
          state.repliesToComments[action.meta.arg.commentId]?.data ?? [];
        if (action.meta.arg.xMsContinuationToken) {
          repliesToComments?.push(...action.payload.data);
        } else {
          repliesToComments = [...action.payload.data];
        }
        state.repliesToComments[action.meta.arg.commentId] = {
          data: repliesToComments,
          xMsContinuationToken: action.payload?.xMsContinuationToken,
        };
        state.loadingRepliesToComments[action.meta.arg.commentId] = false;
      })

      .addCase(insertReplyToCommentOnStory.pending, (state, action) => {
        state.loadingInsertReplyToComment[action.meta.arg.commentId] = true;
      })
      .addCase(insertReplyToCommentOnStory.rejected, (state, action) => {
        state.loadingInsertReplyToComment[action.meta.arg.commentId] = false;
      })
      .addCase(insertReplyToCommentOnStory.fulfilled, (state, action) => {
        state.loadingInsertReplyToComment[action.meta.arg.commentId] = false;

        if (!state.repliesToComments[action.meta.arg.commentId])
          state.repliesToComments[action.meta.arg.commentId] = {
            data: [],
          };
        state.repliesToComments[action.meta.arg.commentId].data.unshift(
          action.payload
        );

        const parentComment = state.comments[action.meta.arg.storyId].data.find(
          (comment) => comment.id === action.meta.arg.commentId
        );
        if (parentComment) {
          parentComment.commentCount++;
        }
      })

      .addCase(likeStoryComment.pending, (state, action) => {
        const sentiment = action.meta.arg.likeDto.sentiment;
        const commentIndex = action.meta.arg.parentCommentId
          ? state.repliesToComments[
              action.meta.arg.parentCommentId
            ].data.findIndex((reply) => reply.id === action.meta.arg.commentId)
          : state.comments[action.meta.arg.storyId].data.findIndex(
              (reply) => reply.id === action.meta.arg.commentId
            );
        const commentItem = action.meta.arg.parentCommentId
          ? state.repliesToComments[action.meta.arg.parentCommentId].data[
              commentIndex
            ]
          : state.comments[action.meta.arg.storyId].data[commentIndex];

        incrementDecrementSentiment(
          commentItem,
          sentiment,
          commentItem.member?.memberLike ?? false
        );
        likeUnlikeComment(
          commentItem,
          !(commentItem.member?.memberLikeValue === sentiment)
        );
        if (commentItem.member) {
          commentItem.member.memberLikeValue = sentiment;
        }
        if (action.meta.arg.parentCommentId) {
          state.repliesToComments[action.meta.arg.parentCommentId].data[
            commentIndex
          ] = commentItem;
          if (state.focusedReplyToStoryComment) {
            state.focusedReplyToStoryComment = commentItem;
          }
        } else {
          state.comments[action.meta.arg.storyId].data[
            commentIndex
          ] = commentItem;
          if (state.focusedComment) {
            state.focusedComment = commentItem;
          }
          if (state.selectedComment) {
            state.selectedComment = commentItem;
          }
        }
      })
      .addCase(likeStoryComment.fulfilled, (state, action) => {
        if (!action.meta.arg.parentCommentId) {
          // comment on story
          const commentsToStory =
            state.comments[action.meta.arg.storyId]?.data ?? [];
          const indexToReplace = commentsToStory.findIndex(
            (comment) => comment.id === action.meta.arg.commentId
          );
          commentsToStory[indexToReplace] = action.payload;
          if (state.focusedComment) state.focusedComment = action.payload;
          if (state.selectedComment) state.selectedComment = action.payload;
        } else {
          // reply to comment on story
          const repliesToComment =
            state.repliesToComments[action.meta.arg.parentCommentId]?.data ??
            [];
          const indexToReplace = repliesToComment.findIndex(
            (comment) => comment.id === action.meta.arg.commentId
          );
          repliesToComment[indexToReplace] = action.payload;
        }
      })

      .addCase(moderatorDeleteCommentOnStory.fulfilled, (state, action) => {
        let removedComment: StoryComment | undefined;

        if (action.meta.arg.parentCommentId) {
          // reply to comment
          removedComment = state.repliesToComments[
            action.meta.arg.parentCommentId
          ].data.find((comment) => comment.id === action.payload.id);
        } else {
          // comment on story
          removedComment = state.comments[action.payload.storyId].data.find(
            (comment) => comment.id === action.payload.id
          );
          if (
            state.repliesToComments &&
            state.repliesToComments[action.payload.id]
          ) {
            state.repliesToComments[action.payload.id].data = [];
          }
        }
        if (removedComment) {
          removedComment.content = action.payload.content;
        }
      })

      .addCase(deleteCommentOnStory.pending, (state, action) => {
        const commentToDeleteId = action.meta.arg.commentId;

        if (action.meta.arg.parentCommentId) {
          const repliesToComment =
            state.repliesToComments[action.meta.arg.parentCommentId].data;
          state.repliesToComments[
            action.meta.arg.parentCommentId
          ].data = repliesToComment.filter(
            (reply) => reply.id !== commentToDeleteId
          );

          // we decrement the replies count of the parent comment
          const parentComment = state.comments[
            action.meta.arg.storyId
          ].data.find(
            (comment) => comment.id === action.meta.arg.parentCommentId
          );
          if (parentComment && parentComment.commentCount > 0) {
            parentComment.commentCount = parentComment.commentCount - 1;
          }
        } else {
          const commentsToStory =
            state.comments[action.meta.arg.storyId]?.data ?? [];
          state.comments[action.meta.arg.storyId].data = commentsToStory.filter(
            (reply) => reply.id !== commentToDeleteId
          );
        }
      })
      .addCase(fetchStoryComment.fulfilled, (state, action) => {
        state.focusedComment = action.payload;
      })
      .addCase(fetchStoryComment.rejected, (state) => {
        state.focusedComment = undefined;
      })
      .addCase(fetchSelectedStoryComment.fulfilled, (state, action) => {
        state.selectedComment = action.payload;
        state.selectedCommentLoading = false;
      })
      .addCase(fetchSelectedStoryComment.pending, (state) => {
        state.selectedCommentLoading = true;
        state.selectedComment = undefined;
        state.selectedCommentError = undefined;
      })
      .addCase(fetchSelectedStoryComment.rejected, (state, action) => {
        state.selectedComment = undefined;
        state.selectedCommentError = action.payload;
        state.selectedCommentLoading = false;
      })
      .addCase(fetchReplyToStoryComment.fulfilled, (state, action) => {
        state.focusedReplyToStoryComment = action.payload;
      })
      .addCase(fetchReplyToStoryComment.rejected, (state) => {
        state.focusedReplyToStoryComment = undefined;
      })
      .addMatcher(
        isAnyOf(
          updateCommentOnStory.fulfilled,
          moderatorUpdateCommentOnStory.fulfilled
        ),
        (state, action) => {
          if (action.meta.arg.parentCommentId) {
            const repliesToComment =
              state.repliesToComments[action.meta.arg.parentCommentId].data;
            const editedReply = repliesToComment.find((reply) => {
              return reply.id === action.meta.arg.commentId;
            });
            if (editedReply) {
              editedReply.content = action.meta.arg.replyDto.content;
            }
          } else {
            const commentsToStory =
              state.comments[action.meta.arg.storyId]?.data ?? [];
            const editedComment = commentsToStory.find((comment) => {
              return comment.id === action.meta.arg.commentId;
            });
            if (editedComment) {
              editedComment.content = action.meta.arg.replyDto.content;
              state.selectedComment = editedComment;
            }
          }
          state.loadingUpdateComment[action.meta.arg.storyId] = false;
        }
      )
      .addMatcher(
        isAnyOf(
          updateCommentOnStory.pending,
          moderatorUpdateCommentOnStory.pending
        ),
        (state, action) => {
          state.loadingUpdateComment[action.meta.arg.storyId] = true;
        }
      )
      .addMatcher(
        isAnyOf(
          updateCommentOnStory.rejected,
          moderatorUpdateCommentOnStory.rejected
        ),
        (state, action) => {
          state.loadingUpdateComment[action.meta.arg.storyId] = false;
        }
      );
  },
});

export const {
  clearComments,
  clearRepliesToCommentStory,
  clearFocusedStoryComment,
  clearSelectedStoryComment,
  clearFocusedReplyToStoryComment,
  incrementStoryCommentRepliesCount,
  decrementStoryCommentRepliesCount,
  setEditedCommentOnStory,
} = storiesCommentsSlice.actions;

export default storiesCommentsSlice.reducer;
